import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { Mail, Phone, MapPin, Clock, ArrowUpRight, CheckCircle2 } from "lucide-react";

import { useLang } from "@/context/LanguageContext";
import { Reveal } from "@/components/motion/Reveal";
import SectionHeading from "@/components/SectionHeading";
import { submitConsultation } from "@/lib/api";

const EMPTY = {
  name: "",
  email: "",
  company: "",
  phone: "",
  industry: "",
  company_size: "",
  service_interest: "",
  budget: "",
  message: "",
};

const inputCls =
  "w-full bg-transparent border border-white/10 text-[#F0F4F8] placeholder:text-[#94A3B8]/50 px-4 py-3.5 text-sm focus:outline-none focus:border-cyan-precision focus-cyan transition-colors";
const labelCls = "block font-mono-tech text-[11px] uppercase tracking-widest text-[#94A3B8] mb-2.5";

export default function Contact() {
  const { t } = useLang();
  const c = t.contact;
  const [form, setForm] = useState(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.company || !form.message) {
      toast.error(c.errorMsg);
      return;
    }
    setSubmitting(true);
    try {
      await submitConsultation(form);
      setDone(true);
      setForm(EMPTY);
      toast.success(c.successTitle);
    } catch (err) {
      toast.error(c.errorMsg);
    } finally {
      setSubmitting(false);
    }
  };

  const info = [
    { icon: Mail, v: c.email },
    { icon: Phone, v: c.phone },
    { icon: MapPin, v: c.address },
    { icon: Clock, v: c.hours },
  ];

  return (
    <div data-testid="contact-page">
      <section className="relative pt-40 pb-16 overflow-hidden">
        <div
          className="absolute -top-20 right-1/4 w-[600px] h-[600px] pointer-events-none"
          style={{ background: "radial-gradient(circle, rgba(0,229,255,0.12), transparent 60%)" }}
        />
        <div className="relative max-w-[1320px] mx-auto px-6 lg:px-10">
          <SectionHeading eyebrow={c.heroEyebrow} title={c.heroTitle} sub={c.heroSub} />
        </div>
      </section>

      <section className="pb-32">
        <div className="max-w-[1320px] mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-8">
          {/* Info column */}
          <div className="lg:col-span-4">
            <Reveal>
              <div className="border border-white/10 bg-navy-surface/40 p-8 lg:p-10 h-full">
                <h3 className="font-mono-tech text-[12px] uppercase tracking-widest text-cyan-precision">
                  {c.infoTitle}
                </h3>
                <ul className="mt-8 space-y-6">
                  {info.map((it, i) => {
                    const Icon = it.icon;
                    return (
                      <li key={i} className="flex items-center gap-4">
                        <div className="w-10 h-10 border border-white/10 flex items-center justify-center text-cyan-precision shrink-0">
                          <Icon size={17} strokeWidth={1.5} />
                        </div>
                        <span className="text-[#F0F4F8]/85 text-sm">{it.v}</span>
                      </li>
                    );
                  })}
                </ul>
                <div className="mt-10 pt-8 border-t border-white/10">
                  <p className="font-display text-xl font-light text-[#F0F4F8] leading-snug">
                    {t.brand.tagline}
                  </p>
                </div>
              </div>
            </Reveal>
          </div>

          {/* Form column */}
          <div className="lg:col-span-8">
            <Reveal delay={0.1}>
              <div className="border border-white/10 bg-navy-surface/40 p-8 lg:p-12">
                <h3 className="font-display text-2xl font-light text-[#F0F4F8] mb-8">{c.formTitle}</h3>

                <AnimatePresence mode="wait">
                  {done ? (
                    <motion.div
                      key="success"
                      data-testid="form-success"
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="py-16 text-center"
                    >
                      <CheckCircle2 size={56} className="mx-auto text-cyan-precision" strokeWidth={1.2} />
                      <h4 className="mt-6 font-display text-3xl font-light text-[#F0F4F8]">
                        {c.successTitle}
                      </h4>
                      <p className="mt-3 text-[#94A3B8] max-w-md mx-auto">{c.successBody}</p>
                      <button
                        data-testid="send-another"
                        onClick={() => setDone(false)}
                        className="mt-8 font-mono-tech text-[12px] uppercase tracking-widest text-cyan-precision border border-cyan-precision/40 px-6 py-3 hover:bg-cyan-precision hover:text-navy transition-colors"
                      >
                        {c.successAgain}
                      </button>
                    </motion.div>
                  ) : (
                    <motion.form
                      key="form"
                      onSubmit={handleSubmit}
                      data-testid="consultation-form"
                      className="grid grid-cols-1 sm:grid-cols-2 gap-6"
                    >
                      <div>
                        <label className={labelCls}>{c.fields.name} *</label>
                        <input
                          data-testid="input-name"
                          className={inputCls}
                          value={form.name}
                          onChange={set("name")}
                          placeholder="—"
                        />
                      </div>
                      <div>
                        <label className={labelCls}>{c.fields.email} *</label>
                        <input
                          data-testid="input-email"
                          type="email"
                          className={inputCls}
                          value={form.email}
                          onChange={set("email")}
                          placeholder="—"
                        />
                      </div>
                      <div>
                        <label className={labelCls}>{c.fields.company} *</label>
                        <input
                          data-testid="input-company"
                          className={inputCls}
                          value={form.company}
                          onChange={set("company")}
                          placeholder="—"
                        />
                      </div>
                      <div>
                        <label className={labelCls}>{c.fields.phone}</label>
                        <input
                          data-testid="input-phone"
                          className={inputCls}
                          value={form.phone}
                          onChange={set("phone")}
                          placeholder="—"
                        />
                      </div>
                      <div>
                        <label className={labelCls}>{c.fields.industry}</label>
                        <input
                          data-testid="input-industry"
                          className={inputCls}
                          value={form.industry}
                          onChange={set("industry")}
                          placeholder="—"
                        />
                      </div>
                      <div>
                        <label className={labelCls}>{c.fields.size}</label>
                        <select
                          data-testid="select-size"
                          className={`${inputCls} appearance-none cursor-pointer`}
                          value={form.company_size}
                          onChange={set("company_size")}
                        >
                          <option value="" className="bg-navy-surface">
                            {c.fields.sizePlaceholder}
                          </option>
                          {c.sizes.map((s) => (
                            <option key={s} value={s} className="bg-navy-surface">
                              {s}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className={labelCls}>{c.fields.service}</label>
                        <select
                          data-testid="select-service"
                          className={`${inputCls} appearance-none cursor-pointer`}
                          value={form.service_interest}
                          onChange={set("service_interest")}
                        >
                          <option value="" className="bg-navy-surface">
                            {c.fields.servicePlaceholder}
                          </option>
                          {c.services.map((s) => (
                            <option key={s} value={s} className="bg-navy-surface">
                              {s}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className={labelCls}>{c.fields.budget}</label>
                        <select
                          data-testid="select-budget"
                          className={`${inputCls} appearance-none cursor-pointer`}
                          value={form.budget}
                          onChange={set("budget")}
                        >
                          <option value="" className="bg-navy-surface">
                            {c.fields.budgetPlaceholder}
                          </option>
                          {c.budgets.map((b) => (
                            <option key={b} value={b} className="bg-navy-surface">
                              {b}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="sm:col-span-2">
                        <label className={labelCls}>{c.fields.message} *</label>
                        <textarea
                          data-testid="input-message"
                          rows={5}
                          className={`${inputCls} resize-none`}
                          value={form.message}
                          onChange={set("message")}
                          placeholder={c.fields.messagePlaceholder}
                        />
                      </div>
                      <div className="sm:col-span-2">
                        <button
                          data-testid="submit-consultation"
                          type="submit"
                          disabled={submitting}
                          className="w-full sm:w-auto inline-flex items-center justify-center gap-2 font-mono-tech text-[13px] uppercase tracking-widest bg-cyan-precision text-navy px-8 py-4 hover:bg-cyan-bright transition-colors disabled:opacity-50 disabled:cursor-not-allowed cyan-glow"
                        >
                          {submitting ? c.submitting : c.submit}
                          {!submitting && <ArrowUpRight size={16} />}
                        </button>
                      </div>
                    </motion.form>
                  )}
                </AnimatePresence>
              </div>
            </Reveal>
          </div>
        </div>
      </section>
    </div>
  );
}
